/**
 * PendingConnectionsDropdown
 *
 * A dropdown menu in the navbar that displays pending connection requests
 * with accept/decline actions for each request.
 *
 * Follows the pattern established by PendingInvitationsDropdown.tsx
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Check,
  X,
  Clock,
  User,
  AlertCircle,
  Loader2,
  ChevronRight,
  Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  usePendingReceived,
  usePendingReceivedCount,
  useConnectionActions,
  useConnectionLoading,
  useConnectionError,
} from '@/stores/connection';
import type { PendingConnectionRequest } from '@/types/connections';

// Maximum items to show in dropdown
const MAX_DROPDOWN_ITEMS = 5;

interface PendingConnectionsDropdownProps {
  className?: string;
}

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a requester
 */
const getDisplayName = (requester: PendingConnectionRequest['requester']): string => {
  if (requester.firstName && requester.lastName) {
    return `${requester.firstName} ${requester.lastName}`;
  }
  if (requester.firstName) {
    return requester.firstName;
  }
  return requester.username;
};

/**
 * PendingConnectionsDropdown
 *
 * Navbar dropdown showing pending connection requests with quick actions.
 */
export const PendingConnectionsDropdown: React.FC<PendingConnectionsDropdownProps> = ({
  className,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const pendingRequests = usePendingReceived();
  const pendingCount = usePendingReceivedCount();
  const { isLoading } = useConnectionLoading();
  const error = useConnectionError();
  const {
    fetchPendingReceived,
    acceptRequest,
    declineRequest,
    clearError,
  } = useConnectionActions();

  // Fetch pending requests when dropdown opens (force refresh to bypass cache)
  useEffect(() => {
    if (open) {
      console.log('[NavbarConnections] Dropdown opened - fetching pending requests (force)');
      fetchPendingReceived(true);
    }
  }, [open, fetchPendingReceived]);

  // Handle accept request
  const handleAccept = async (request: PendingConnectionRequest) => {
    setProcessingId(request.connectionId);
    clearError();

    const displayName = getDisplayName(request.requester);

    try {
      console.log('[NavbarConnections] Accepting request from:', displayName);

      await acceptRequest(request.connectionId);

      toast.success(`Connected with ${displayName}!`, {
        description: 'You are now connected.',
      });

      // Navigate to connections if that was the last request
      if (pendingCount === 1) {
        navigate('/dashboard/connections');
        setOpen(false);
      }
    } catch (err) {
      console.error('[NavbarConnections] Error accepting request:', err);
      toast.error('Failed to accept request', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Handle decline request
  const handleDecline = async (request: PendingConnectionRequest) => {
    setProcessingId(request.connectionId);
    clearError();

    const displayName = getDisplayName(request.requester);

    try {
      console.log('[NavbarConnections] Declining request from:', displayName);

      await declineRequest(request.connectionId);

      toast.success('Request declined', {
        description: `You have declined the connection request from ${displayName}.`,
      });
    } catch (err) {
      console.error('[NavbarConnections] Error declining request:', err);
      toast.error('Failed to decline request', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Format expiry date
  const formatExpiry = (expiresAt: string): string => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return 'Expired';
    } else if (diffDays === 1) {
      return 'Expires tomorrow';
    } else if (diffDays <= 7) {
      return `Expires in ${diffDays} days`;
    } else {
      return `Expires ${expiry.toLocaleDateString()}`;
    }
  };

  // Check if request is expiring soon (within 24 hours)
  const isExpiringSoon = (expiresAt: string): boolean => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    return diffMs > 0 && diffMs < 24 * 60 * 60 * 1000;
  };

  // Display count for badge
  const displayCount = pendingCount > 99 ? '99+' : pendingCount;

  // Requests to show (limited for dropdown)
  const displayedRequests = pendingRequests.slice(0, MAX_DROPDOWN_ITEMS);
  const hasMore = pendingRequests.length > MAX_DROPDOWN_ITEMS;

  console.log('[NavbarConnections] Pending count:', pendingCount);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'relative p-2 hover:bg-accent rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
            className
          )}
          aria-label={
            pendingCount > 0
              ? `${pendingCount} pending connection request${pendingCount !== 1 ? 's' : ''}`
              : 'No pending connection requests'
          }
        >
          <Link2 className="h-5 w-5" aria-hidden="true" />

          {/* Notification badge */}
          {pendingCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-primary text-primary-foreground animate-in zoom-in-50 duration-200"
              aria-hidden="true"
            >
              {displayCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[380px] max-h-[480px] overflow-hidden"
        sideOffset={8}
      >
        {/* Header */}
        <DropdownMenuLabel className="flex items-center justify-between py-3 px-4">
          <span className="text-base font-semibold">Connection Requests</span>
          {pendingCount > 0 && (
            <Badge
              variant="secondary"
              className="text-xs bg-primary/10 text-primary"
            >
              {pendingCount} pending
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-0" />

        {/* Content */}
        <div className="overflow-y-auto max-h-[380px]">
          {/* Loading state */}
          {isLoading && pendingRequests.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                Loading requests...
              </span>
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <AlertCircle className="h-8 w-8 mb-2 text-destructive" />
              <p className="text-sm text-center text-muted-foreground mb-3">
                {error}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchPendingReceived()}
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && pendingRequests.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <Users className="h-10 w-10 mb-2 text-muted-foreground opacity-50" />
              <p className="text-sm text-muted-foreground text-center">
                No pending requests
              </p>
              <p className="text-xs text-muted-foreground/70 text-center mt-1">
                When someone sends you a connection request, it will appear here.
              </p>
            </div>
          )}

          {/* Request list */}
          {!error && displayedRequests.length > 0 && (
            <div className="divide-y divide-border">
              {displayedRequests.map((request) => (
                <ConnectionRequestCard
                  key={request.connectionId}
                  request={request}
                  onAccept={() => handleAccept(request)}
                  onDecline={() => handleDecline(request)}
                  isProcessing={processingId === request.connectionId}
                  formatExpiry={formatExpiry}
                  isExpiringSoon={isExpiringSoon}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer - View all link */}
        <DropdownMenuSeparator className="my-0" />
        <div className="p-2">
          <Button
            variant="ghost"
            className="w-full justify-between text-sm"
            onClick={() => {
              navigate(pendingCount > 0 ? '/dashboard/connections?tab=pending' : '/dashboard/connections');
              setOpen(false);
            }}
          >
            <span>
              {hasMore
                ? `View all ${pendingCount} requests`
                : 'View all connections'}
            </span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * ConnectionRequestCard
 *
 * Individual connection request item within the dropdown.
 */
interface ConnectionRequestCardProps {
  request: PendingConnectionRequest;
  onAccept: () => void;
  onDecline: () => void;
  isProcessing: boolean;
  formatExpiry: (expiresAt: string) => string;
  isExpiringSoon: (expiresAt: string) => boolean;
}

const ConnectionRequestCard: React.FC<ConnectionRequestCardProps> = ({
  request,
  onAccept,
  onDecline,
  isProcessing,
  formatExpiry,
  isExpiringSoon,
}) => {
  const expiryText = formatExpiry(request.expiresAt);
  const expiringSoon = isExpiringSoon(request.expiresAt);
  const displayName = getDisplayName(request.requester);
  const initials = getInitials(
    request.requester.firstName,
    request.requester.lastName,
    request.requester.username
  );

  return (
    <div
      className={cn(
        'p-4 hover:bg-accent/50 transition-colors',
        isProcessing && 'opacity-60 pointer-events-none'
      )}
    >
      {/* Requester info */}
      <div className="flex items-start gap-3 mb-3">
        {/* Avatar */}
        <Avatar className="h-10 w-10 flex-shrink-0">
          <AvatarImage src={request.requester.avatar} alt={displayName} />
          <AvatarFallback className="text-sm font-medium bg-primary/10 text-primary">
            {initials}
          </AvatarFallback>
        </Avatar>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-sm truncate">{displayName}</h4>
            {request.requester.mutualConnectionsCount !== undefined &&
             request.requester.mutualConnectionsCount > 0 && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 h-5"
              >
                <Users className="h-2.5 w-2.5 mr-1" />
                {request.requester.mutualConnectionsCount} mutual
              </Badge>
            )}
          </div>

          {/* Username */}
          <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
            <User className="h-3 w-3" />
            <span>@{request.requester.username}</span>
          </div>
        </div>
      </div>

      {/* Custom message */}
      {request.message && (
        <div className="text-xs rounded-md p-2 mb-3 italic bg-muted/50 text-muted-foreground">
          "{request.message}"
        </div>
      )}

      {/* Expiry and actions */}
      <div className="flex items-center justify-between gap-2">
        {/* Expiry indicator */}
        <div
          className={cn(
            'flex items-center gap-1 text-xs',
            expiringSoon ? 'text-orange-600' : 'text-muted-foreground'
          )}
        >
          <Clock className="h-3 w-3" />
          <span>{expiryText}</span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDecline();
            }}
            disabled={isProcessing}
            className="h-7 px-2 text-xs"
          >
            {isProcessing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <X className="h-3 w-3 mr-1" />
            )}
            Decline
          </Button>

          <Button
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAccept();
            }}
            disabled={isProcessing}
            className="h-7 px-2 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isProcessing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Check className="h-3 w-3 mr-1" />
            )}
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PendingConnectionsDropdown;
